/**
 * Mixin for persisting widget editor UI state (active tab, expanded panels) in session storage.
 * Allows the editor to restore the user's position when they navigate away and return.
 * 
 * Usage:
 * 1. Import and add to mixins array
 * 2. Set `editor_state_key` in data() to a unique identifier (e.g., form ID, settings ID)
 * 3. Ensure your component has: active_tab, expanded_general, expanded_styles in data()
 * 
 * Example:
 * import WidgetEditorStatePersistence from '@/mixins/widget/widget-editor-state-persistence.js';
 * 
 * export default {
 *   mixins: [WidgetEditorStatePersistence],
 *   data() {
 *     return {
 *       editor_state_key: this.$route.params.form_id,
 *       active_tab: 0,
 *       expanded_general: [0],
 *       expanded_styles: [0],
 *     };
 *   },
 * };
 */

export default {
  data() {
    return {
      // override this in your component with a unique identifier
      editor_state_key: null,
    };
  },

  created() {
    this.active_tab = this.restoreActiveTab();
    this.expanded_general = this.restoreExpandedState('expanded_general', [0]);
    this.expanded_styles = this.restoreExpandedState('expanded_styles', [0]);
  },

  watch: {
    active_tab(newVal) {
      this.saveActiveTab(newVal);
    },

    expanded_general: {
      handler(newVal) {
        this.saveExpandedState('expanded_general', newVal);
      },
      deep: true,
    },

    expanded_styles: {
      handler(newVal) {
        this.saveExpandedState('expanded_styles', newVal);
      },
      deep: true,
    },
  },

  methods: {
    /**
     * Generates a unique session storage key for this editor instance
     * @param {string} key - The specific key to store (e.g., 'active_tab')
     * @returns {string} The full session storage key
     */
    getSessionStorageKey(key) {
      if (!this.editor_state_key) {
        return `widget_editor_${key}`;
      }
      return `widget_editor_${this.editor_state_key}_${key}`;
    },

    /**
     * Restores the active tab from session storage
     * @returns {number} The saved tab index or 0 if not found
     */
    restoreActiveTab() {
      try {
        const stored = sessionStorage.getItem(this.getSessionStorageKey('active_tab'));
        return stored !== null ? parseInt(stored, 10) : 0;
      } catch (e) {
        return 0;
      }
    },

    /**
     * Saves the active tab to session storage
     * @param {number} value - The tab index to save
     */
    saveActiveTab(value) {
      try {
        sessionStorage.setItem(this.getSessionStorageKey('active_tab'), value.toString());
      } catch (e) {
        // silently fail if e.g. private browsing
      }
    },

    /**
     * Restores an expanded state array from session storage
     * @param {string} key - The key to restore (e.g., 'expanded_general')
     * @param {Array} defaultValue - The default value if nothing is stored
     * @returns {Array} The saved state or default value
     */
    restoreExpandedState(key, defaultValue) {
      try {
        const stored = sessionStorage.getItem(this.getSessionStorageKey(key));
        return stored !== null ? JSON.parse(stored) : defaultValue;
      } catch (e) {
        return defaultValue;
      }
    },

    /**
     * Saves an expanded state array to session storage
     * @param {string} key - The key to save (e.g., 'expanded_general')
     * @param {Array} value - The state array to save
     */
    saveExpandedState(key, value) {
      try {
        sessionStorage.setItem(this.getSessionStorageKey(key), JSON.stringify(value));
      } catch (e) {
        // silently fail if e.g. private browsing
      }
    },
  },
};
